@if (patient$ | async; as patient) {
  <div class="flex items-center gap-2">
    <div class="flex-auto">
      <div class="flex flex-wrap items-end gap-x-2 gap-y-1">
        <a
          class="min-w-0 break-words text-xl font-bold"
          [prBrandRouterLink]="['patients', patient.ref.id]"
          [ngClass]="{
            'text-red-500': patient.status | map: isDisabledPatient
          }"
        >
          {{ patient?.name }}
        </a>
        <div class="leading-none">
          <pr-new-patient-icon iconSize="icon-md" [patient]="patient" />
        </div>
        @if (patient.status !== patientStatus.Active) {
          <pr-patient-status-chip
            class="self-center"
            [status]="patient.status"
          />
        }
        <pr-patient-metadata-display-outlet
          [patient]="patient"
          [displayTarget]="metadataTargetPatientName"
        />
      </div>
      <div>
        {{ patient.gender | splitCamel | titlecase }} &bull;
        <ng-container *ngTemplateOutlet="dateOfBirth" />
      </div>
    </div>

    <button
      *ngIf="expandable"
      mat-icon-button
      class="icon-md"
      (click)="content.toggle()"
    >
      <mat-icon>
        {{ content.expanded ? 'expand_less' : 'expand_more' }}
      </mat-icon>
    </button>

    <ng-container *ngIf="actions$ | async as actions">
      <button
        *ngIf="actions.length"
        mat-icon-button
        class="icon-md"
        [matMenuTriggerFor]="patientMenu"
      >
        <mat-icon>more_vert</mat-icon>
      </button>
    </ng-container>
  </div>

  <pt-collapsible #content [expanded]="expanded$ | async">
    <ng-template ptCollapsibleContent>
      <div class="mt-2 flex gap-4">
        <div class="flex flex-auto flex-col gap-2 overflow-x-hidden">
          <div class="grid grid-cols-2">
            <div class="flex flex-col">
              <ng-container *ngTemplateOutlet="email" />
              <ng-container *ngTemplateOutlet="phoneNumber" />
            </div>
            <div
              *ngIf="bloc.healthFundName$ | async as healthFundName"
              class="max-w-[150px] pl-2"
            >
              <div class="text-xs opacity-80">Health Fund</div>
              <div
                class="truncate text-sm"
                [ptTruncateTooltip]="healthFundName"
              >
                {{ healthFundName }}
              </div>
            </div>
          </div>

          <ng-content />

          <div class="flex gap-2">
            <div class="w-1/2">
              <ng-container *ngTemplateOutlet="accountOutstanding" />
            </div>
            <div class="w-1/2">
              <ng-container *ngTemplateOutlet="accountCredit" />
            </div>
          </div>

          <div
            ptIntercomTarget="patient-profile-summary.recalls-and-campaigns"
            class="flex flex-col rounded-md bg-slate-50 px-2 py-1 text-slate-500"
          >
            <ng-container *ngTemplateOutlet="recallInfo" />
            <ng-container *ngTemplateOutlet="activeWorkflowItems" />
            <ng-container *ngTemplateOutlet="marketingCampaigns" />
          </div>

          <ng-container *ngIf="bloc.tags$ | async as tags">
            <pr-tag-display-list
              *ngIf="tags.length"
              class="inline-block"
              [tags]="tags"
              [compact]="true"
            />
          </ng-container>

          <ng-content select="pr-appointment-tags-sidebar-list" />
          <pr-patient-metadata-display-outlet
            [patient]="patient"
            [displayTarget]="metadataTargetProfileCard"
          />

          @if (patient.depositRequired) {
            <pr-patient-deposit-required-badge
              [depositRequired]="patient.depositRequired"
            />
          }
        </div>

        <pt-user-icon
          [src]="patient | profileImage$ | async"
          [name]="patient.name"
          [diameter]="100"
        />
      </div>
    </ng-template>
  </pt-collapsible>

  <ng-template #dateOfBirth>
    <ng-container *ngIf="patient.dateOfBirth; else noDateOfBirth">
      {{ patient.dateOfBirth | moment | amDateFormat: bloc.dobFormat }}
      &bull; {{ bloc.age$ | async }}
    </ng-container>
  </ng-template>

  <ng-template #noDateOfBirth>
    <span class="text-xs opacity-50">No date of birth</span>
  </ng-template>

  <ng-template #phoneNumber>
    <ng-container
      *ngIf="bloc.phoneNumber$ | async as phoneNumber; else noPhoneNumber"
    >
      <ng-container
        *ngIf="phoneActions$ | async as phoneActions; else phoneNoLink"
      >
        <ng-container *ngIf="phoneActions.length > 1; else phoneNoLink">
          <a
            class="truncate"
            [routerLink]="[]"
            [matMenuTriggerFor]="phoneMenu"
            >{{ phoneNumber }}</a
          >
        </ng-container>
      </ng-container>

      <ng-template #phoneNoLink>
        <span class="truncate"> {{ phoneNumber }}</span>
      </ng-template>
    </ng-container>
  </ng-template>

  <ng-template #noPhoneNumber>
    <span class="text-xs opacity-50">No phone number</span>
  </ng-template>

  <ng-template #email>
    <ng-container *ngIf="patient.email; else noEmail">
      <ng-container
        *ngIf="emailAction$ | async as emailAction; else emailNoLink"
      >
        <a
          *ngIf="(emailAction.isDisabled$ | async) === false; else emailNoLink"
          class="truncate"
          [routerLink]="[]"
          (click)="emailAction.run()"
          >{{ patient.email }}</a
        >
      </ng-container>

      <ng-template #emailNoLink>
        <span class="truncate">{{ patient.email }}</span>
      </ng-template>
    </ng-container>
  </ng-template>

  <ng-template #noEmail>
    <span class="text-xs opacity-50">No email</span>
  </ng-template>

  <ng-template #accountOutstanding>
    <div class="rounded-md bg-red-50 px-2 py-1 text-red-500">
      <div class="-mb-0.5 text-xs">Outstanding</div>
      <div
        *ngIf="(bloc.account.isLoading$ | async) === false; else accountLoader"
        class="text-base font-bold"
      >
        {{ bloc.account.outstanding$ | async | currency }}
      </div>
    </div>
  </ng-template>

  <ng-template #accountCredit>
    <div class="rounded-md bg-emerald-50 px-2 py-1 text-emerald-500">
      <div class="-mb-0.5 text-xs">Credit</div>
      <div
        *ngIf="(bloc.account.isLoading$ | async) === false; else accountLoader"
        class="text-base font-bold"
      >
        {{ bloc.account.creditRemaining$ | async | currency }}
      </div>
    </div>
  </ng-template>

  <ng-template #recallInfo>
    @for (track of enabledTracks(); track track) {
      @let recall = getRecall(patient, track);
      <div class="flex flex-row items-center gap-2">
        <mat-icon class="icon-sm">event_repeat</mat-icon>
        <div class="text-xs">
          @if (enabledTracks().length > 1) {
            <strong>{{ trackLabel(track) }}:</strong>
          }
          @if (recall?.isDisabled) {
            Recalls disabled
          } @else {
            Next recall
            @if (nextRecallAppointment(track); as recallAppointment) {
              @if (recallAppointment.event?.from) {
                booked for
                <a
                  href="javascript:void(0);"
                  (click)="timelineNav.showOnTimeline(recallAppointment)"
                >
                  {{
                    recallAppointment.event?.from
                      | moment
                      | amDateFormat: dateFormat
                  }}
                </a>
              }
            } @else {
              @if (recall?.nextRecallDue?.isSet) {
                due
                {{
                  recall?.nextRecallDue?.dueAt
                    | moment
                    | amDateFormat: dateFormat
                }}
              } @else {
                date is not set
              }
            }
          }
        </div>
      </div>
    }
  </ng-template>

  <ng-template #marketingCampaigns>
    <div class="flex flex-row items-center gap-2">
      <mat-icon class="icon-sm">campaign</mat-icon>
      <div class="text-xs">
        <a href="javascript:void(0);" (click)="openPatientCampaigns()">
          @if (activeCampaignEntryCount() > 0) {
            {{ activeCampaignEntryCount() }} active
          } @else {
            No active
          }
        </a>
        campaign entries
      </div>
    </div>
  </ng-template>

  <ng-template #activeWorkflowItems>
    @if (
      hasWorkflowDialogProvider && (activeTaskCount() || activeLabJobCount())
    ) {
      <div
        class="flex flex-row items-center gap-2"
        [ngClass]="{
          'bg-red-50 text-red-600': hasOverdueActiveWorkflowItem()
        }"
      >
        <mat-icon class="icon-sm">
          {{ hasOverdueActiveWorkflowItem() ? 'warning' : 'assignment' }}
        </mat-icon>
        <div class="text-xs">
          @if (activeTaskCount()) {
            <a
              href="javascript:void(0);"
              (click)="openActiveWorkflowItems('tasks')"
            >
              @if (overdueTaskCount()) {
                {{ overdueTaskCount() }} overdue
              } @else {
                {{ activeTaskCount() }}
              }
              {{ activeTaskCount() === 1 ? 'task' : 'tasks' }}
            </a>
          }
          @if (activeTaskCount() && activeLabJobCount()) {
            &bull;
          }
          @if (activeLabJobCount()) {
            <a
              href="javascript:void(0);"
              (click)="openActiveWorkflowItems('labJobs')"
            >
              @if (overdueLabJobCount()) {
                {{ overdueLabJobCount() }} overdue
              } @else {
                {{ activeLabJobCount() }}
              }
              {{ activeLabJobCount() === 1 ? 'lab job' : 'lab jobs' }}
            </a>
          }
        </div>
      </div>
    }
  </ng-template>

  <ng-template #accountLoader>
    <mat-spinner color="primary" [diameter]="24" />
  </ng-template>

  <mat-menu #patientMenu="matMenu">
    <button
      *ngFor="let action of actions$ | async; trackBy: trackByAction"
      mat-menu-item
      [disabled]="action.isDisabled$ | async"
      (click)="action.run()"
    >
      <mat-icon *ngIf="action.icon">{{ action.icon }}</mat-icon>
      <span>{{ action.name }}</span>
    </button>
  </mat-menu>

  <mat-menu #phoneMenu="matMenu">
    <button
      *ngFor="let action of phoneActions$ | async; trackBy: trackByAction"
      mat-menu-item
      [disabled]="action.isDisabled$ | async"
      (click)="action.run()"
    >
      <mat-icon *ngIf="action.icon">{{ action.icon }}</mat-icon>
      <span>{{ action.name }}</span>
    </button>
  </mat-menu>
}
